<script setup>
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import { computed, ref, watch, onMounted } from "vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import ImageInput from "./ImageInput.vue";

const props = defineProps({
    provider: {
        type: Object,
        required: true,
    },
    companies: {
        type: Array,
        default: () => [],
    },
    services: {
        type: Array,
        default: () => [],
    },
    showBackButton: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['saved', 'cancel']);

const page  = usePage();
const toast = useToast();
const errors = ref({});

const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');

const providerTypes = [
    { label: 'Person',   value: 'person'  },
    { label: 'Team',     value: 'team'    },
    { label: 'Virtual',  value: 'virtual' },
];

const filteredServices    = ref([...props.services]);
const companyWorkingHours = ref(null);

const form = useForm({
    company_id:          props.provider.company_id,
    name:                props.provider.name,
    email:               props.provider.email,
    phone:               props.provider.phone,
    provider_type:       props.provider.provider_type,
    description:         props.provider.description,
    capacity:            props.provider.capacity,
    is_active:           props.provider.is_active,
    visible_in_admin:    props.provider.visible_in_admin,
    visible_in_schedule: props.provider.visible_in_schedule,
    services:            props.provider.services?.map(s => s.id) ?? [],
    image:               props.provider.image,
    sort_order:          props.provider.sort_order,
});

// ── Fetch company working hours ────────────────────────────────────────
const fetchCompanyHours = async (companyId) => {
    if (!companyId) return;
    try {
        const { data } = await axios.get(`/ajax/admin/company-working-hours?company_id=${companyId}`);
        companyWorkingHours.value = data.data || [];
    } catch (e) {
        console.error('Failed to fetch company working hours', e);
    }
};

onMounted(() => {
    fetchCompanyHours(form.company_id);
});

// ── Re-fetch services when company changes (super_admin only) ──────────
watch(() => form.company_id, async (newId) => {
    if (newId) {
        try {
            const { data } = await axios.get(`/ajax/admin/services?company_id=${newId}&all=1`);
            filteredServices.value = data.data || data;
        } catch (e) {
            console.error('Failed to fetch services', e);
            filteredServices.value = [];
        }

        if (newId !== props.provider.company_id) {
            await fetchCompanyHours(newId);
            if (companyWorkingHours.value) {
                form.working_hours = companyWorkingHours.value.map(h => ({
                    day_of_week: h.day_of_week,
                    is_workday:  h.is_workday,
                    start_time:  h.start_time,
                    end_time:    h.end_time,
                }));
            }
        }
    } else {
        filteredServices.value = props.services;
    }
}, { immediate: true });

// ── Submit ─────────────────────────────────────────────────────────────
const submit = () => {
    errors.value = {};
    form.put(`/management/providers/${props.provider.uuid}`, {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Mitarbeiter wurde erfolgreich aktualisiert.',
                life: 5000,
            });
            emit('saved');
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben.',
                life: 5000,
            });
        },
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));
</script>

<template>
    <div class="text-sm">

        <!-- Error banner -->
        <Message v-if="hasErrors" severity="error" class="mb-6">
            <div v-for="(error, key) in errors" :key="key">{{ error }}</div>
        </Message>

        <form @submit.prevent="submit" class="space-y-8" novalidate>
            <div class="space-y-8">

                <!-- Section 1: Profile -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                        <h2 class="text-lg font-bold text-gray-700">Profilinformationen</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">

                        <!-- Company (super_admin only) -->
                        <div v-if="isSuperAdmin" class="flex flex-col gap-2">
                            <label for="pef-company_id" class="font-bold text-gray-600">Unternehmen *</label>
                            <Dropdown id="pef-company_id"
                                      v-model="form.company_id"
                                      :options="companies"
                                      optionLabel="name"
                                      optionValue="id"
                                      placeholder="Unternehmen wählen"
                                      :class="{'p-invalid': errors.company_id}"
                                      filter
                                      class="w-full"
                                      @change="errors.company_id = null" />
                            <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-provider_type" class="font-bold text-gray-600">Typ *</label>
                            <Dropdown id="pef-provider_type"
                                      v-model="form.provider_type"
                                      :options="providerTypes"
                                      optionLabel="label"
                                      optionValue="value"
                                      class="w-full"
                                      @change="errors.provider_type = null" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-name" class="font-bold text-gray-600">Name *</label>
                            <InputText id="pef-name" v-model="form.name"
                                       :class="{'p-invalid': errors.name}" class="w-full" @input="errors.name = null" />
                            <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-email" class="font-bold text-gray-600">E-Mail</label>
                            <InputText id="pef-email" type="email" v-model="form.email"
                                       :class="{'p-invalid': errors.email}" class="w-full" @input="errors.email = null" />
                            <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-phone" class="font-bold text-gray-600">Telefon</label>
                            <InputText id="pef-phone" v-model="form.phone" class="w-full" @input="errors.phone = null" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-capacity" class="font-bold text-gray-600">Kapazität (Gleichzeitige Buchungen)</label>
                            <InputNumber id="pef-capacity"
                                         v-model="form.capacity"
                                         :min="1"
                                         class="w-full"
                                         inputClass="w-full"
                                         showButtons @input="errors.capacity = null" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="pef-sort_order" class="font-bold text-gray-600">Sortierreihenfolge</label>
                            <InputNumber id="pef-sort_order"
                                         v-model="form.sort_order"
                                         class="w-full"
                                         inputClass="w-full"
                                         showButtons @input="errors.sort_order = null" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <ImageInput v-model="form.image" label="Bild" :error="errors.image" />
                        </div>
                    </div>
                </div>

                <!-- Section 2: Services -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                        <h2 class="text-lg font-bold text-gray-700">Zugeordnete Dienstleistungen</h2>
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="pef-services" class="font-bold text-gray-600">Angebotene Dienstleistungen</label>
                        <MultiSelect id="pef-services"
                                     v-model="form.services"
                                     :options="filteredServices"
                                     optionLabel="name"
                                     optionValue="id"
                                     placeholder="Dienstleistungen auswählen"
                                     filter
                                     :maxSelectedLabels="2"
                                     :selectedItemsLabel="form.services.length + ' Dienstleistungen ausgewählt'"
                                     class="w-full max-w-full" @change="errors.services = null" />
                    </div>
                </div>

                <!-- Section 3: Visibility & Description -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                        <h2 class="text-lg font-bold text-gray-700">Sichtbarkeit & Beschreibung</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="pef-is_active" v-model="form.is_active" :binary="true" />
                            <label for="pef-is_active" class="font-bold text-gray-700 cursor-pointer">Aktiv</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="pef-visible_in_admin" v-model="form.visible_in_admin" :binary="true" />
                            <label for="pef-visible_in_admin" class="font-bold text-gray-700 cursor-pointer">Sichtbar in Admin</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="pef-visible_in_schedule" v-model="form.visible_in_schedule" :binary="true" />
                            <label for="pef-visible_in_schedule" class="font-bold text-gray-700 cursor-pointer">Sichtbar im Kalender</label>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="pef-description" class="font-bold text-gray-600">Beschreibung</label>
                        <Textarea id="pef-description" v-model="form.description" rows="4" class="w-full" />
                    </div>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="flex justify-between items-center pt-8 border-t mt-10">
                <Button v-if="showBackButton"
                        type="button"
                        label="Abbrechen"
                        severity="secondary"
                        outlined
                        class="px-8 py-3 rounded-xl border-gray-300 font-bold"
                        @click="emit('cancel')" />
                <div v-else></div>

                <Button type="submit"
                        class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200"
                        :loading="form.processing"
                        :disabled="form.processing">
                    <i class="pi pi-check" />
                    Änderungen speichern
                </Button>
            </div>
        </form>
    </div>
</template>
